/**
 * ActivateSceneDialog - Dialog for activating a scene with duration options
 */

import {
  useActivateScene,
  getListScenesQueryKey,
  getGetSceneQueryKey,
  type ScenesSceneResponse,
  type ScenesSceneSummaryResponse,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import { Clock, Infinity as InfinityIcon, Timer, AlertCircle } from 'lucide-react';
import { useState } from 'react';

import { Modal, Button, TextField, RadioGroup, Select, useToast } from '@/components/ui';
import {
  formatDurationShort,
  toSeconds,
  DURATION_UNIT_OPTIONS,
  type DurationUnit,
} from '@/features/scenes/utils/duration';

type Scene = ScenesSceneResponse | ScenesSceneSummaryResponse;

interface ActivateSceneDialogProps {
  scene: Scene;
  isOpen: boolean;
  onClose: () => void;
  isAlreadyActive?: boolean;
}

type DurationOption = 'default' | 'no_expiry' | 'custom';

// Presets include a quick "30s" for testing, then common minute/hour options
const PRESET_DURATIONS = [
  { value: '30', unit: 'seconds' as DurationUnit, label: '30s' },
  { value: '5', unit: 'minutes' as DurationUnit, label: '5 min' },
  { value: '10', unit: 'minutes' as DurationUnit, label: '10 min' },
  { value: '30', unit: 'minutes' as DurationUnit, label: '30 min' },
  { value: '1', unit: 'hours' as DurationUnit, label: '1 hour' },
];

export function ActivateSceneDialog({
  scene,
  isOpen,
  onClose,
  isAlreadyActive = false,
}: ActivateSceneDialogProps) {
  const [durationOption, setDurationOption] = useState<DurationOption>('default');
  const [customValue, setCustomValue] = useState('10');
  const [customUnit, setCustomUnit] = useState<DurationUnit>('minutes');
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { mutate: activateScene, isPending } = useActivateScene({
    mutation: {
      onSuccess: () => {
        addToast({
          message: isAlreadyActive
            ? `Timer reset for "${scene.name}"`
            : `Scene "${scene.name}" activated`,
          variant: 'success',
        });
        queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
        if (scene.id) {
          queryClient.invalidateQueries({ queryKey: getGetSceneQueryKey(scene.id) });
        }
        onClose();
      },
      onError: (err) => {
        const message = err instanceof Error ? err.message : 'Failed to activate scene';
        setError(message);
      },
    },
  });

  const handleActivate = () => {
    if (!scene.id) return;

    setError(null);

    let body: { durationSeconds?: number } = {};

    switch (durationOption) {
      case 'default':
        // No body - use scene default
        break;
      case 'no_expiry':
        // Pass 0 to indicate no expiry
        body = { durationSeconds: 0 };
        break;
      case 'custom': {
        const value = parseInt(customValue, 10);
        if (isNaN(value) || value <= 0) {
          setError('Please enter a valid duration');
          return;
        }
        body = { durationSeconds: toSeconds(value, customUnit) };
        break;
      }
    }

    activateScene({
      id: scene.id,
      data: body,
    });
  };

  const handlePresetClick = (value: string, unit: DurationUnit) => {
    setCustomValue(value);
    setCustomUnit(unit);
  };

  const getDefaultDurationDescription = () => {
    if (!scene.durationSeconds) {
      return 'No duration configured - scene will stay active until manually deactivated';
    }
    if (scene.durationMode === 'fromTriggerEnd') {
      return `Scene will deactivate ${formatDurationShort(scene.durationSeconds)} after triggers end. For manual activation, no expiry will be set.`;
    }
    return `Scene will auto-deactivate after ${formatDurationShort(scene.durationSeconds)}`;
  };

  const durationOptions = [
    {
      value: 'default',
      label: 'Use scene default',
      description: getDefaultDurationDescription(),
    },
    {
      value: 'no_expiry',
      label: 'No expiry',
      description: 'Scene stays active until manually deactivated',
    },
    {
      value: 'custom',
      label: 'Custom duration',
      description: 'Set a specific duration for this activation',
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isAlreadyActive ? 'Extend / Reset Timer' : 'Activate Scene'}
      subtitle={scene.name ?? ''}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleActivate} loading={isPending}>
            {isAlreadyActive ? 'Reset Timer' : 'Activate'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <RadioGroup
          name="duration"
          label="Duration"
          value={durationOption}
          onChange={(v) => setDurationOption(v as DurationOption)}
          options={durationOptions}
        />

        {durationOption === 'custom' && (
          <div className="ml-6 space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-zinc-800/50">
            {/* Presets */}
            <div className="flex flex-wrap gap-2">
              {PRESET_DURATIONS.map((preset) => (
                <button
                  type="button"
                  key={`${preset.value}-${preset.unit}`}
                  onClick={() => handlePresetClick(preset.value, preset.unit)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    customValue === preset.value && customUnit === preset.unit
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-600'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Custom input */}
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <TextField
                  label="Duration"
                  type="number"
                  min={1}
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  icon={<Timer className="h-4 w-4" />}
                />
              </div>
              <Select
                label="Unit"
                hideLabel
                value={customUnit}
                onChange={(v) => setCustomUnit(v as DurationUnit)}
                options={DURATION_UNIT_OPTIONS}
                className="w-28"
              />
            </div>
          </div>
        )}

        {/* Visual indicators */}
        <div className="flex items-center gap-4 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:bg-zinc-800/50 dark:text-zinc-400">
          {durationOption === 'no_expiry' ? (
            <>
              <InfinityIcon className="h-5 w-5 text-gray-400 dark:text-zinc-500" />
              <span>Scene will remain active until manually deactivated</span>
            </>
          ) : durationOption === 'custom' ? (
            <>
              <Clock className="h-5 w-5 text-blue-500" />
              <span>
                Scene will auto-deactivate after{' '}
                {formatDurationShort(toSeconds(parseInt(customValue, 10) || 0, customUnit))}
              </span>
            </>
          ) : (
            <>
              <Clock className="h-5 w-5 text-gray-400 dark:text-zinc-500" />
              <span>Using scene&apos;s configured duration settings</span>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
